import React, { useState } from "react";
import {
  FaTimes,
  FaExchangeAlt,
  FaCommentDots,
  FaStar,
  FaBox,
  FaHome,
  FaPercent,
  FaChevronDown,
  FaHeadset,
  FaRulerCombined,
  FaShieldAlt,
} from "react-icons/fa";
import "../styles/CancellationReturns.css";

const CancellationReturns = () => {
  const [activeTopic, setActiveTopic] = useState("cancellation");
  const [openFaq, setOpenFaq] = useState(null);

  // Static sidebar options - ONLY relevant to Lungies, Towels, Bedsheets
  const helpTopics = [
    "Delivery related",
    "My Account",
    "Refunds related",
    "Payment Options",
    "Returns & Pickup",
    "Cancellation related",
    "Product Quality",
    "Size & Exchange",
    "Order Tracking",
    "KKB Care (Support)",
  ];

  const cancellationFaqs = [
    {
      question:
        "Can I modify the size/color of a Lungi, Towel, or Bedsheet without cancelling?",
      answer:
        "No, modifications to product specifications (size, color, design, quantity) are not possible once an order is placed. You will need to cancel the existing order and place a new one with your desired specifications.",
    },
    {
      question:
        "Why am I getting charged for cancellation? / What is cancellation fee?",
      answer:
        "For KKB products (Lungies, Towels, Bedsheets), cancellation is FREE within 30 minutes for prepaid orders and 60 minutes for COD orders. After that, a nominal fee of ₹29 or 3% of order value (whichever is lower) may be charged to cover processing and restocking costs.",
    },
    {
      question: "I see the 'Cancel' button but I can't click on it. Why?",
      answer:
        'This happens when your order has already been shipped from our warehouse. Once the order status changes to "Shipped", cancellation is not possible. You can simply reject the delivery at your doorstep for a full refund (no questions asked).',
    },
    {
      question: "How long does it take to cancel an order?",
      answer:
        "Cancellation is processed instantly for prepaid orders. For COD orders, cancellation happens immediately. Refunds for prepaid orders are initiated within 24 hours and reflect in your bank/UPI within 3-7 business days.",
    },
    {
      question: "Can I reinstate a cancelled order?",
      answer:
        "No, cancelled orders cannot be reinstated. You will need to place a fresh order for the Lungi, Towel, or Bedsheet you wish to purchase.",
    },
    {
      question: "What is the free cancellation period for KKB products?",
      answer:
        "The free cancellation period is clearly shown on your Order Details page. Typically: 30 minutes for prepaid orders, 60 minutes for COD orders. You can also find this in your order confirmation email.",
    },
    {
      question: "Can I cancel only one item from a multi-item order?",
      answer:
        'Yes, you can cancel individual items from your order. Go to My Orders → Select the specific Lungi/Towel/Bedsheet → Click "Cancel Item". The remaining items will be delivered as usual.',
    },
  ];

  const returnsFaqs = [
    {
      question: "What is KKB return policy for Lungies, Towels, and Bedsheets?",
      answer:
        "KKB offers a 10-day replacement guarantee for manufacturing defects and a 15-day return window for unopened, unused products with original packaging and tags. Due to hygiene reasons, opened towels and bedsheets cannot be returned unless there is a manufacturing defect.",
    },
    {
      question: "How do I initiate a return?",
      answer:
        'Go to My Orders → Select the product → Click on "Return" → Choose a reason (size issue, defect, wrong product) → Select pickup address → Submit. Our logistics partner will pick up the item within 2-3 business days.',
    },
    {
      question: "How long does refund take after return pickup?",
      answer:
        "Once the returned item is received and quality checked (1-2 days), your refund is processed within 5-7 business days. Refunds go to original payment method or as KKB Store Credits (instant, no fees).",
    },
    {
      question:
        "Can I exchange a bedsheet or towel for a different color/size?",
      answer:
        'Yes, exchanges for size, color, or design are available within 10 days of delivery, provided the product is unused, unwashed, and has all original tags and packaging. Visit "My Orders" and select "Exchange" instead of "Return".',
    },
    {
      question: "What if I receive a damaged or defective Lungi/Towel?",
      answer:
        'If you receive a damaged or defective product, please report it within 48 hours of delivery. Go to My Orders → "Report an Issue" → Upload photos of the defect. We will arrange a free pickup and send a replacement immediately.',
    },
    {
      question: "Can I return a product without original packaging?",
      answer:
        "Original packaging is required for returns. Please keep the KKB packaging and tags intact until you are sure about keeping the product. Returns without original packaging may be rejected or subject to a restocking fee.",
    },
  ];

  const activeFaqs =
    activeTopic === "cancellation" ? cancellationFaqs : returnsFaqs;

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="help-center-container">
      {/* Header Section */}
      <div className="header-section">
        <div className="row header-row">
          <h1 className="main-title">
            KKB Help Center | 24x7 Customer Care Support
          </h1>
          <div className="col">
            <div className="hero-text">
              <p>
                The <strong>KKB Help Centre</strong> page lists out various
                types of issues that you may have encountered so that there can
                be quick resolution and you can go back to shopping online for
                authentic
                <strong> Lungies, Towels, and Bedsheets</strong> —{" "}
                <em>Tradition through Generations</em>.
                <br />
                <br />
                Get information regarding order tracking, delivery date changes,
                returns, refunds, cancellations, and product quality assistance.
                This information is updated on 03-May-26.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="row main-content-row">
        {/* Main Content - FAQ Area */}
        <div className="main-content">
          {/* Active Topic Title */}
          <div className="active-topic-title">
            {activeTopic === "cancellation" ? (
              <>
                <FaTimes style={{ marginRight: "8px" }} /> Cancellation related
              </>
            ) : (
              <>
                <FaExchangeAlt style={{ marginRight: "8px" }} /> Returns &
                Pickup related
              </>
            )}
          </div>

          {/* FAQ Items */}
          <div className="faq-list">
            {activeFaqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <div
                  className={`faq-question ${openFaq === index ? "open" : ""}`}
                  onClick={() => toggleFaq(index)}
                >
                  <p>{faq.question}</p>
                  <FaChevronDown
                    className={`faq-arrow ${openFaq === index ? "rotated" : ""}`}
                    style={{ transition: "transform 0.3s ease" }}
                  />
                </div>
                <div
                  className={`faq-answer ${openFaq === index ? "visible" : ""}`}
                >
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Help Section - KKB Specific */}
          <div className="additional-help">
            <div className="help-card">
              <span className="help-icon">
                <FaExchangeAlt />
              </span>
              <h3>Need to return a Lungi/Towel/Bedsheet?</h3>
              <p>
                Contact us to initiate a return. You will receive a confirmation
                call explaining the pickup process.
              </p>
              <button className="help-button">Contact Support</button>
            </div>
            <div className="help-card">
              <span className="help-icon">
                <FaCommentDots />
              </span>
              <h3>Size & Fit Consultation</h3>
              <p>
                Not sure about Lungi sizes or bedsheet dimensions? Our experts
                are here to help 24x7.
              </p>
              <button className="help-button">Live Chat Now</button>
            </div>
            <div className="help-card">
              <span className="help-icon">
                <FaStar />
              </span>
              <h3>Quality Assurance</h3>
              <p>
                All KKB products go through strict quality checks. Report any
                issues within 48 hours.
              </p>
              <button className="help-button">Report Issue</button>
            </div>
          </div>

          {/* Quick Info Banner */}
          <div className="quick-info">
            <div className="info-item">
              <span className="info-emoji">
                <FaBox />
              </span>
              <div>
                <strong>Free Cancellation</strong>
                <small>Within 30 mins (Prepaid) | 60 mins (COD)</small>
              </div>
            </div>
            <div className="info-item">
              <span className="info-emoji">
                <FaHome />
              </span>
              <div>
                <strong>Free Pickup</strong>
                <small>For defective/wrong products</small>
              </div>
            </div>
            <div className="info-item">
              <span className="info-emoji">
                <FaPercent />
              </span>
              <div>
                <strong>100% Refund</strong>
                <small>If quality doesn't meet expectations</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancellationReturns;
